import { useRouter } from '@tanstack/react-router';
import { mapApiErrorToForm } from '@upme/api-client';
import {
  type ServiceCategoryDto,
  type ServiceDto,
  UpdateServiceInput,
  type UpdateServiceInput as UpdateServiceInputType,
} from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { type Resolver, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { collectRootMessages } from '../../../app/form-errors';
import { zodResolver } from '../../../app/zod-resolver';
import { useUpdateService } from '../mutations';
import { ServiceCategoryMultiSelect } from './service-category-multi-select';

interface Props {
  service: ServiceDto;
  categories: ReadonlyArray<ServiceCategoryDto>;
}

const FORMAT_LABELS: Record<string, string> = {
  AT_PROVIDER: 'У исполнителя (адрес)',
  AT_CLIENT: 'С выездом к клиенту',
  ONLINE: 'Онлайн',
};

export function ServiceEditForm({ service, categories }: Props) {
  const router = useRouter({ warn: false });

  const form = useForm<UpdateServiceInputType>({
    resolver: zodResolver(
      UpdateServiceInput.loose() as unknown as z.ZodType<UpdateServiceInputType>,
    ) as Resolver<UpdateServiceInputType>,
    defaultValues: {
      title: service.title,
      description: service.description ?? undefined,
      priceText: service.priceText ?? undefined,
      format: service.format,
      addressText: service.addressText ?? undefined,
      categoryIds: service.categories.map((c) => c.id),
      // status — управляется отдельным Select'ом; DRAFT не входит в UpdateServiceInput
      ...(service.status === 'ARCHIVED' || service.status === 'PUBLISHED' ? { status: service.status } : {}),
    } as UpdateServiceInputType,
    mode: 'onSubmit',
  });

  const update = useUpdateService(service.id);

  const format = form.watch('format') ?? service.format;
  const categoryIds = form.watch('categoryIds') ?? [];
  const status = form.watch('status');

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const body: UpdateServiceInputType = {
        title: values.title,
        description: values.description || null,
        priceText: values.priceText || null,
        format: values.format,
        addressText: values.format === 'AT_PROVIDER' ? values.addressText || null : null,
        categoryIds: values.categoryIds,
        ...(values.status ? { status: values.status } : {}),
      };
      await update.mutateAsync(body);
      toast.success('Услуга обновлена');
      router?.navigate({ to: '/services' });
    } catch (err) {
      await mapApiErrorToForm(err, form, { rootField: 'title' });
    }
  });

  const err = (field: keyof UpdateServiceInputType) => {
    const m = form.formState.errors[field];
    return m ? <p className="text-sm text-destructive mt-1">{String(m.message)}</p> : null;
  };
  const rootMessages = collectRootMessages(form.formState.errors as Record<string, unknown>);

  return (
    <form onSubmit={onSubmit} className="space-y-4 max-w-xl" noValidate>
      <div>
        <Label htmlFor="title">Название</Label>
        <Input id="title" {...form.register('title')} />
        {err('title')}
      </div>

      <div>
        <Label htmlFor="description">Описание</Label>
        <textarea
          id="description"
          rows={4}
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
          {...form.register('description')}
        />
        {err('description')}
      </div>

      <div>
        <Label htmlFor="priceText">Цена (текст)</Label>
        <Input id="priceText" {...form.register('priceText')} placeholder="от 1500 ₽" />
        {err('priceText')}
      </div>

      <div>
        <Label>Формат</Label>
        <Select
          value={format}
          onValueChange={(v) => form.setValue('format', v as ServiceDto['format'], { shouldValidate: true })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(['AT_PROVIDER', 'AT_CLIENT', 'ONLINE'] as const).map((f) => (
              <SelectItem key={f} value={f}>
                {FORMAT_LABELS[f]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {err('format')}
      </div>

      {format === 'AT_PROVIDER' && (
        <div>
          <Label htmlFor="addressText">Адрес</Label>
          <Input id="addressText" {...form.register('addressText')} />
          {err('addressText')}
        </div>
      )}

      <div>
        <Label>Категории</Label>
        <ServiceCategoryMultiSelect
          categories={categories}
          value={categoryIds}
          onChange={(next) => form.setValue('categoryIds', next, { shouldValidate: true })}
        />
        {err('categoryIds')}
      </div>

      <div>
        <Label>Статус</Label>
        <Select
          value={status ?? service.status}
          onValueChange={(v) => form.setValue('status', v as 'ARCHIVED', { shouldValidate: true })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {service.status === 'DRAFT' && (
              <SelectItem value="DRAFT" disabled>
                Черновик
              </SelectItem>
            )}
            {service.status === 'PENDING_MODERATION' && (
              <SelectItem value="PENDING_MODERATION" disabled>
                На модерации
              </SelectItem>
            )}
            {service.status === 'PUBLISHED' && (
              <SelectItem value="PUBLISHED" disabled>
                Опубликована
              </SelectItem>
            )}
            <SelectItem value="ARCHIVED">В архиве</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          Публикация — через очередь модерации (кнопка «Одобрить»). Здесь услугу можно отправить в архив.
        </p>
      </div>

      {rootMessages.map((m) => (
        <p key={m} className="text-sm text-destructive">
          {m}
        </p>
      ))}

      <Button type="submit" disabled={form.formState.isSubmitting}>
        Сохранить
      </Button>
    </form>
  );
}
